import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import useOrganizationService from '../../../app/services/hooks/useOrganizationService';
import PageHeader from '../../../components/PageHeader/PageHeader';
import DefaultCard from '../../../components/default/DefaultCard';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import { OrganizationData, PlanSummary } from '../types';
import SummaryTab from './tabs/SummaryTab';
import TeamTab from './tabs/TeamTab';
import BrandingTab from './tabs/BrandingTab';
import EmailsTab from './tabs/EmailsTab';
import SubscriptionTab from './tabs/SubscriptionTab';
import DataTab from './tabs/DataTab';
import RolesTab from './tabs/RolesTab';
import { formatDate } from '../types';

interface MembershipInfo {
    id: number;
    role: string;
    role_display: string;
    is_admin: boolean;
    is_owner: boolean;
}

export interface OrganizationContextProps {
    organization: OrganizationData;
    membership: MembershipInfo;
    plan: PlanSummary;
    reload: () => void;
    goToTab: (tab: string) => void;
}

const TABS = [
    { key: 'summary', label: 'Resumen', icon: 'fa-chart-pie', adminOnly: false },
    { key: 'team', label: 'Equipo', icon: 'fa-users', adminOnly: false },
    { key: 'roles', label: 'Roles', icon: 'fa-user-shield', adminOnly: false, firmOnly: true },
    { key: 'branding', label: 'Marca', icon: 'fa-palette', adminOnly: false },
    { key: 'emails', label: 'Correos', icon: 'fa-envelope', adminOnly: true },
    { key: 'subscription', label: 'Suscripción', icon: 'fa-credit-card', adminOnly: false },
    { key: 'data', label: 'Datos', icon: 'fa-id-card', adminOnly: false }
];

/**
 * Mi Estudio: lo mismo para un estudio jurídico que para un abogado
 * independiente (organización de un solo usuario). El equipo solo aplica a estudios.
 */
const MyOrganization: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const { getMyOrganization, fetchingMyOrganization, acceptDataProcessing, fetchingDpa } = useOrganizationService();

    const [organization, setOrganization] = useState<OrganizationData | null>(null);
    const [membership, setMembership] = useState<MembershipInfo | null>(null);
    const [plan, setPlan] = useState<PlanSummary | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);

    const params = new URLSearchParams(location.search);
    const requestedTab = params.get('tab') || 'summary';

    const load = () => {
        getMyOrganization({
            onSuccess: (response) => {
                setOrganization(response.data.organization);
                setMembership(response.data.membership);
                setPlan(response.data.plan);
                setLoadError(null);
            },
            onError: (response) => setLoadError(response.message || 'No se pudo cargar tu estudio')
        });
    };

    useEffect(() => {
        load();
    }, []);

    // Mensajes al volver de Flow (registro de tarjeta)
    useEffect(() => {
        if (params.get('success') === 'subscribed') {
            toast.success('¡Suscripción activada! Tu estudio ya tiene el nuevo plan.');
        } else if (params.get('error')) {
            toast.error('No se pudo completar la suscripción en Flow. Intenta de nuevo.');
        }
    }, []);

    const handleAcceptDpa = () => {
        acceptDataProcessing({
            onSuccess: (response) => {
                toast.success(response.message);
                load();
            },
            onError: (response) => toast.error(response.message)
        });
    };

    const goToTab = (tab: string) => history.push(`/intranet/my-organization?tab=${tab}`);

    if (loadError) {
        return (
            <DefaultCard>
                <div className="text-center py-5">
                    <i className="fas fa-building fa-3x text-secondary mb-3" />
                    <h5>{loadError}</h5>
                </div>
            </DefaultCard>
        );
    }

    if (fetchingMyOrganization && !organization) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    if (!organization || !membership || !plan) return null;

    const isFirm = organization.organization_type === 'firm';
    const tabs = TABS.filter((tab) => (!tab.adminOnly || membership.is_admin) && (!tab.firmOnly || isFirm));
    // Una pestaña que este usuario no tiene (?tab=roles en un independiente) vuelve al resumen
    const activeTab = tabs.some((tab) => tab.key === requestedTab) ? requestedTab : 'summary';
    const context: OrganizationContextProps = { organization, membership, plan, reload: load, goToTab };

    return (
        <div className="container-fluid">
            <PageHeader
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: isFirm ? 'Mi Estudio' : 'Mi Cuenta Profesional', isCurrent: true }
                ]}
                title={organization.name}
                description={[
                    organization.organization_type_display,
                    // En una cuenta independiente el rol no aporta (siempre es el titular)
                    organization.organization_type === 'independent' ? null : `Tu rol: ${membership.role_display}`,
                    plan.plan?.name ? `Plan ${plan.plan.name}` : 'Sin plan asignado',
                ].filter(Boolean).join(' · ')}
            />

            {organization.status === 'suspended' && (
                <div className="alert alert-danger d-flex align-items-start gap-2">
                    <i className="fas fa-ban mt-1" />
                    <div>
                        <strong>Tu estudio está suspendido.</strong> Puedes ver tus datos y los de tus clientes, pero no
                        hacer cambios. {organization.suspension_reason && <>Motivo: {organization.suspension_reason}.</>}{' '}
                        Contacta a Monere para reactivarlo.
                    </div>
                </div>
            )}

            {organization.status === 'closed' && (
                <div className="alert alert-danger d-flex flex-wrap justify-content-between align-items-center gap-2" data-testid="closure-alert">
                    <span>
                        <i className="fas fa-door-open me-2" />
                        <strong>{isFirm ? 'Tu estudio está dado de baja.' : 'Tu cuenta está dada de baja.'}</strong> Queda en solo
                        lectura y sus datos se eliminarán el <strong>{formatDate(organization.purge_after)}</strong>.
                    </span>
                    {membership.is_admin && (
                        <button type="button" className="btn btn-sm btn-light" onClick={() => goToTab('data')}>
                            Descargar datos o revertir la baja
                        </button>
                    )}
                </div>
            )}

            {membership.is_admin && organization.dpa_accepted === false && organization.status !== 'closed' && (
                <div className="alert alert-warning d-flex flex-wrap justify-content-between align-items-center gap-2" data-testid="dpa-banner">
                    <span>
                        <i className="fas fa-file-signature me-2" />
                        Para cumplir la Ley 19.628 (art. 15 bis), acepta el{' '}
                        <a href="/contrato-encargo" target="_blank" rel="noopener noreferrer">contrato de encargo de tratamiento de datos</a>:
                        {isFirm ? ' tu estudio es' : ' eres'} responsable de los datos de tus clientes y Monere los trata solo por tu cuenta.
                        {organization.dpa_version && <> Aceptaste la versión {organization.dpa_version}; hay una versión nueva.</>}
                    </span>
                    <ButtonGeneral onClick={handleAcceptDpa} title="Aceptar contrato de encargo" size="small"
                                   icon="fa-check" loading={fetchingDpa} />
                </div>
            )}

            {plan.is_fallback && plan.subscription?.status === 'morose' && (
                <div className="alert alert-warning">
                    <i className="fas fa-exclamation-triangle me-2" />
                    Tu suscripción tiene un pago pendiente. Mientras tanto aplican los límites del plan gratuito.
                </div>
            )}

            <ul className="nav nav-tabs mb-3" role="tablist">
                {tabs.map((tab) => (
                    <li className="nav-item" key={tab.key} role="presentation">
                        <button
                            type="button"
                            className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                            onClick={() => goToTab(tab.key)}
                            role="tab"
                            aria-selected={activeTab === tab.key}
                            data-testid={`tab-${tab.key}`}
                        >
                            <i className={`fas ${tab.icon} me-2`} />
                            {tab.key === 'team' && !isFirm ? 'Usuarios' : tab.label}
                        </button>
                    </li>
                ))}
            </ul>

            {activeTab === 'summary' && <SummaryTab {...context} />}
            {activeTab === 'team' && <TeamTab {...context} />}
            {activeTab === 'roles' && isFirm && <RolesTab {...context} />}
            {activeTab === 'branding' && <BrandingTab {...context} />}
            {activeTab === 'emails' && membership.is_admin && <EmailsTab {...context} />}
            {activeTab === 'subscription' && <SubscriptionTab {...context} />}
            {activeTab === 'data' && <DataTab {...context} />}
        </div>
    );
};

export default MyOrganization;
